import React from 'react';

// Generic skeleton pulse animation
const Pulse: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-800 rounded ${className}`} />
);

// Card skeleton
export const CardSkeleton: React.FC = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
    <div className="flex items-center gap-3 mb-4">
      <Pulse className="w-10 h-10 rounded-full" />
      <div className="flex-1">
        <Pulse className="h-4 w-24 mb-2" />
        <Pulse className="h-3 w-16" />
      </div>
    </div>
    <Pulse className="h-4 w-full mb-2" />
    <Pulse className="h-4 w-3/4 mb-2" />
    <Pulse className="h-4 w-1/2" />
  </div>
);

// Post skeleton
export const PostSkeleton: React.FC = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
    <div className="flex items-start gap-3 mb-4">
      <Pulse className="w-10 h-10 rounded-full flex-shrink-0" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Pulse className="h-4 w-24" />
          <Pulse className="h-3 w-16" />
        </div>
        <Pulse className="h-3 w-20" />
      </div>
    </div>
    <Pulse className="h-4 w-full mb-2" />
    <Pulse className="h-4 w-full mb-2" />
    <Pulse className="h-4 w-2/3 mb-4" />
    <div className="flex items-center gap-4 pt-4 border-t border-slate-800">
      <Pulse className="h-6 w-16" />
      <Pulse className="h-6 w-16" />
      <Pulse className="h-6 w-16" />
    </div>
  </div>
);

// User card skeleton
export const UserCardSkeleton: React.FC = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
    <Pulse className="w-12 h-12 rounded-full" />
    <div className="flex-1">
      <Pulse className="h-4 w-32 mb-2" />
      <Pulse className="h-3 w-20" />
    </div>
    <Pulse className="h-8 w-20 rounded-lg" />
  </div>
);

// Event/News card skeleton
export const EventCardSkeleton: React.FC = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
    <Pulse className="h-40 w-full" />
    <div className="p-4">
      <Pulse className="h-5 w-3/4 mb-3" />
      <Pulse className="h-4 w-full mb-2" />
      <Pulse className="h-4 w-2/3 mb-4" />
      <div className="flex items-center gap-2">
        <Pulse className="h-6 w-24" />
        <Pulse className="h-6 w-20" />
      </div>
    </div>
  </div>
);

// Table row skeleton
export const TableRowSkeleton: React.FC<{ columns?: number }> = ({ columns = 5 }) => (
  <tr>
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Pulse className="h-4 w-full" />
      </td>
    ))}
  </tr>
);

// Profile header skeleton
export const ProfileHeaderSkeleton: React.FC = () => (
  <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
    <div className="flex flex-col md:flex-row items-center gap-6">
      <Pulse className="w-24 h-24 rounded-full" />
      <div className="flex-1 text-center md:text-left">
        <Pulse className="h-6 w-48 mb-2 mx-auto md:mx-0" />
        <Pulse className="h-4 w-32 mb-4 mx-auto md:mx-0" />
        <Pulse className="h-4 w-full max-w-md mb-4" />
        <div className="flex gap-4 justify-center md:justify-start">
          <Pulse className="h-8 w-24" />
          <Pulse className="h-8 w-24" />
          <Pulse className="h-8 w-24" />
        </div>
      </div>
    </div>
  </div>
);

// Stats cards skeleton
export const StatsSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
        <Pulse className="h-8 w-16 mb-2" />
        <Pulse className="h-4 w-24" />
      </div>
    ))}
  </div>
);

// Grid skeleton for cards
export const GridSkeleton: React.FC<{ count?: number; type?: 'card' | 'post' | 'user' | 'event' }> = ({ 
  count = 6, 
  type = 'card' 
}) => {
  const SkeletonComponent = {
    card: CardSkeleton,
    post: PostSkeleton,
    user: UserCardSkeleton,
    event: EventCardSkeleton,
  }[type];

  return (
    <div className={`grid gap-4 ${type === 'event' ? 'md:grid-cols-2 lg:grid-cols-3' : ''}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonComponent key={i} />
      ))}
    </div>
  );
};

// List skeleton for feed
export const FeedSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <PostSkeleton key={i} />
    ))}
  </div>
);

// Full page skeleton
export const PageSkeleton: React.FC<{ title?: boolean }> = ({ title = true }) => (
  <div className="space-y-6">
    {title && (
      <div className="flex items-center justify-between">
        <Pulse className="h-8 w-48" />
        <Pulse className="h-10 w-32 rounded-lg" />
      </div>
    )}
    <StatsSkeleton />
    <FeedSkeleton count={3} />
  </div>
);

// Detail page skeleton
export const DetailPageSkeleton: React.FC = () => (
  <div className="space-y-6">
    <Pulse className="h-64 w-full rounded-xl" />
    <div className="space-y-4">
      <Pulse className="h-8 w-3/4" />
      <div className="flex gap-4">
        <Pulse className="h-10 w-10 rounded-full" />
        <div>
          <Pulse className="h-4 w-32 mb-2" />
          <Pulse className="h-3 w-24" />
        </div>
      </div>
      <Pulse className="h-4 w-full" />
      <Pulse className="h-4 w-full" />
      <Pulse className="h-4 w-2/3" />
    </div>
  </div>
);

// Inline loader
export const InlineLoader: React.FC<{ text?: string }> = ({ text = 'Loading...' }) => (
  <div className="flex items-center justify-center gap-2 py-8 text-slate-400">
    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-500" />
    <span>{text}</span>
  </div>
);

// Empty state
export const EmptyState: React.FC<{ 
  icon?: React.ReactNode; 
  title: string; 
  description?: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    {icon && <div className="text-slate-600 mb-4">{icon}</div>}
    <h3 className="text-lg font-medium text-slate-300 mb-2">{title}</h3>
    {description && <p className="text-slate-500 mb-4 max-w-sm">{description}</p>}
    {action}
  </div>
);

export default {
  CardSkeleton,
  PostSkeleton,
  UserCardSkeleton,
  EventCardSkeleton,
  TableRowSkeleton,
  ProfileHeaderSkeleton,
  StatsSkeleton,
  GridSkeleton,
  FeedSkeleton,
  PageSkeleton,
  DetailPageSkeleton,
  InlineLoader,
  EmptyState,
};



